import { Input, type InputMask } from "@roguelik/shared-sim";

/**
 * Keyboard -> InputMask (WASD + arrow keys). Lives on the main thread because
 * DOM key events are only available there; the mask is forwarded to the worker.
 */

const KEY_TO_BIT: Readonly<Record<string, number>> = {
  KeyW: Input.Up,
  ArrowUp: Input.Up,
  KeyS: Input.Down,
  ArrowDown: Input.Down,
  KeyA: Input.Left,
  ArrowLeft: Input.Left,
  KeyD: Input.Right,
  ArrowRight: Input.Right,
  // Active ability (B4 moved it off Space, which now toggles manual aim).
  ShiftLeft: Input.Ability,
  KeyQ: Input.Ability,
};

export interface InputSource {
  getMask(): InputMask;
  dispose(): void;
}

/**
 * True when the event originated from (or focus is on) an editable control — a
 * text input, textarea, select, or contenteditable. Movement keys must be ignored
 * there so the lobby/auth form can be typed into (e.g. a username with "w"/"a"/
 * "s"/"d") instead of driving the character.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target instanceof HTMLElement ? target : null;
  if (el === null) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * Attaches key listeners and tracks the current movement mask. `onChange` fires
 * only when the mask actually changes, so callers can send deltas.
 */
export function createInputSource(
  target: Window,
  onChange: (mask: InputMask) => void,
): InputSource {
  let mask: InputMask = Input.None;

  const apply = (code: string, pressed: boolean): void => {
    const bit = KEY_TO_BIT[code];
    if (bit === undefined) return;
    const next = pressed ? mask | bit : mask & ~bit;
    if (next !== mask) {
      mask = next;
      onChange(mask);
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    // Let editable fields (lobby/auth form) receive the keystroke normally.
    if (isEditableTarget(e.target)) return;
    if (e.code in KEY_TO_BIT) {
      e.preventDefault();
      apply(e.code, true);
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (isEditableTarget(e.target)) return;
    apply(e.code, false);
  };
  // Releasing focus should not leave keys "stuck" down.
  const onBlur = (): void => {
    if (mask !== Input.None) {
      mask = Input.None;
      onChange(mask);
    }
  };
  // Focusing a text field (e.g. clicking the lobby form while holding a move key)
  // must also clear the mask, since the matching keyup will be ignored there.
  const onFocusIn = (e: Event): void => {
    if (isEditableTarget(e.target) && mask !== Input.None) {
      mask = Input.None;
      onChange(mask);
    }
  };

  target.addEventListener("keydown", onKeyDown);
  target.addEventListener("keyup", onKeyUp);
  target.addEventListener("blur", onBlur);
  target.addEventListener("focusin", onFocusIn);

  return {
    getMask: () => mask,
    dispose() {
      target.removeEventListener("keydown", onKeyDown);
      target.removeEventListener("keyup", onKeyUp);
      target.removeEventListener("blur", onBlur);
      target.removeEventListener("focusin", onFocusIn);
    },
  };
}
